import React, { useEffect, useRef } from "react"
import {
  Award,
  ShieldCheck,
  Target,
  Heart,
  Users,
  Microscope,
  Building2,
  Quote,
  CheckCircle2,
  Activity,
  Globe,
  Sparkles,
  ChevronRight,
  Compass,
  MapPin
} from "lucide-react"
import { Link } from "react-router-dom"
import team1 from '../assets/viany.png'
import why1 from '../assets/why1.jpeg'
import why2 from '../assets/why2.png'
import why3 from '../assets/why3.png'
import why4 from '../assets/why4.png'
import infra from '../assets/reliable.jpeg'

const About = () => {
  const observerRef = useRef(null)

  useEffect(() => {
    // SEO Implementation
    document.title =
      "About Us | VCNR Hospital - World-Class Clinical Governance"

    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
      metaDescription = document.createElement("meta")
      metaDescription.setAttribute("name", "description")
      document.head.appendChild(metaDescription)
    }
    metaDescription.setAttribute(
      "content",
      "Discover the legacy of VCNR Hospital. Led by Dr. Vinay C.N. Rao, our institution provides world-class tertiary healthcare in Bangalore through specialized clinical institutes and evidence-based medicine."
    )

    // Scroll to top
    window.scrollTo(0, 0)

    // Animation Observer
    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active")
          }
        })
      },
      { threshold: 0.1 }
    )

    document.querySelectorAll(".reveal").forEach(el => {
      observerRef.current?.observe(el)
    })

    return () => {
      observerRef.current?.disconnect()
      document.title = "VCNR Hospital | World-Class Clinical Excellence"
    }
  }, [])

  return (
    <div className="bg-white">
      {/* --- REFINED HERO SECTION --- */}
      <section className="relative pt-48 pb-24 bg-slate-50 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] hospital-grid pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10 animate-fade-in-up">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            {/* <div className="flex items-center gap-2 mb-6 text-vcnr-brand">
              <Link
                to="/"
                className="text-[10px] font-black uppercase tracking-widest hover:text-slate-900 transition-colors"
              >
                Home
              </Link>
              <ChevronRight size={10} className="text-slate-300" />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                About VCNR
              </span>
            </div> */}
            <h1 className="text-5xl md:text-6xl font-semibold   mb-6">
              We Are Defined By <br />
              <span className="text-vcnr-brand">Clinical Integrity.</span>
            </h1>
            <p className="text-slate-600 text-lg mb-8">
              VCNR Hospital is more than a medical facility; it is a specialized
              clinical ecosystem where world-class expertise meets deep-rooted
              human compassion.
            </p>
            <div className="flex flex-wrap justify-center gap-12 pt-10 border-t border-slate-200">
              <div className="flex items-center gap-3">
                <Award className="text-vcnr-brand" size={24} />
                <span className="text-[14px] font-bold uppercase  text-slate-600">
                  NABH Accredited
                </span>
              </div>
              <div className="flex items-center gap-3">
                <ShieldCheck className="text-vcnr-brand" size={24} />
                <span className="text-[14px] font-bold uppercase  text-slate-600">
                  ISO 9001:2015
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Globe className="text-vcnr-brand" size={24} />
                <span className="text-[14px] font-bold uppercase  text-slate-600">
                  Global Standards
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- CHAIRMAN'S MESSAGE --- */}
      <section className="py-24 bg-white reveal">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5 relative group">
              <div className="absolute -inset-4 bg-vcnr-brand/10 rounded-[2.5rem] -rotate-2 group-hover:rotate-0 transition-transform duration-700"></div>
              <img
                src={team1}
                alt="Dr. Vinay C.N. Rao"
                className="relative rounded-[2rem] shadow-2xl grayscale hover:grayscale-0 transition-all duration-700 object-cover w-full h-[600px]"
              />
              <div className="absolute bottom-10 left-10 bg-white/95 backdrop-blur-md p-8 rounded-2xl shadow-xl border border-slate-100 max-w-xs">
                <div className="text-lg font-bold text-slate-900 mb-1">
                  Dr. Vinay C.N. Rao
                </div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-vcnr-brand mb-4">
                  Founder & Chairman
                </div>
                <div className="w-12 h-px bg-vcnr-brand"></div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="text-vcnr-brand mb-8">
                <Quote size={56} className="opacity-20" fill="currentColor" />
              </div>
              <h2 className="text-4xl md:text-[3rem]  font-semibold text-slate-900 mb-8 leading-tight">
                "Healthcare is a{" "}
                <span className="text-vcnr-brand italic">Sacred Trust</span>,
                {" "}{" "}{" "}not Just a Service."
              </h2>
              <div className="text-slate-600 text-lg mb-8">
                <p>
                  At VCNR Hospital, we believe that the true essence of medicine
                  lies in the delicate balance between advanced technology and
                  the human touch. Our journey began with a simple mission: to
                  provide the people of Bangalore with clinical excellence that
                  is accessible, transparent, and patient-centric.
                </p> <br/>
                <p>
                  Today, our 9 specialized institutes stand as a testament to
                  that vision. We have curated a board of specialists who are
                  not only masters of their craft but also compassionate
                  listeners. We don't just treat symptoms; we care for people.
                </p>
                <p className="italic font-bold text-slate-900 pt-6">
                  — Dr. Vinay C.N. Rao
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- VISION & MISSION SECTION --- */}
      <section className="py-24 bg-slate-50 border-y border-slate-200 reveal">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Vision Card */}
            <div className="bg-white p-12 rounded-[2.5rem] shadow-sm border border-slate-100 flex flex-col items-center text-center group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="w-20 h-20 bg-vcnr-brand/10 text-vcnr-brand rounded-3xl flex items-center justify-center mb-8 group-hover:bg-vcnr-brand group-hover:text-white transition-all duration-500 group-hover:rotate-6">
                <Target size={40} />
              </div>
              <h3 className="text-4xl md:text-[3rem]  font-semibold text-slate-900 mb-8 leading-tight">
                Our Vision
              </h3>
              <p className="text-slate-600 text-lg mb-8">
                To be the most trusted and specialized clinical ecosystem,
                setting global benchmarks in tertiary healthcare, patient
                recovery, and evidence-based clinical leadership.
              </p>
            </div>

            {/* Mission Card */}
            <div className="bg-white p-12 rounded-[2.5rem] shadow-sm border border-slate-100 flex flex-col items-center text-center group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="w-20 h-20 bg-vcnr-brand/10 text-vcnr-brand rounded-3xl flex items-center justify-center mb-8 group-hover:bg-vcnr-brand group-hover:text-white transition-all duration-500 group-hover:-rotate-6">
                <Compass size={40} />
              </div>
              <h3 className="text-4xl md:text-[3rem]  font-semibold text-slate-900 mb-8 leading-tight">
                Our Mission
              </h3>
              <p className="text-slate-600 text-lg mb-8">
                To deliver world-class medical excellence through specialized
                multidisciplinary institutes, cutting-edge technology, and a
                compassionate approach that prioritizes patient dignity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- DNA / VALUES SECTION --- */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden reveal">
        <div className="absolute inset-0 opacity-10 hospital-grid pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <span className="text-vcnr-brand text-[12px] font-bold uppercase tracking-[0.1em]">
              The VCNR DNA
            </span>
            <h2 className="text-4xl md:text-[3rem] font-semibold  leading-tight">
              Core Philosophies
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Patient First",
                desc:
                  "Every clinical decision is weighed against the ultimate benefit and comfort of the patient.",
                icon: <Heart size={32} />
              },
              {
                title: "Clinical Excellence",
                desc:
                  "Adhering to evidence-based protocols and international safety standards in every surgery.",
                icon: <Sparkles size={32} />
              },
              {
                title: "Unmatched Integrity",
                desc:
                  "Complete transparency in medical billing, diagnostics, and treatment plans.",
                icon: <ShieldCheck size={32} />
              },
              {
                title: "Continuous Innovation",
                desc:
                  "Investing in the latest robotic and AI-driven diagnostic technologies annually.",
                icon: <Microscope size={32} />
              }
            ].map((value, i) => (
              <div
                key={i}
                className="p-10 border border-white/10 rounded-3xl hover:bg-white/5 transition-all group"
              >
                <div className="mb-6 text-vcnr-brand group-hover:scale-110 transition-transform">
                  {value.icon}
                </div>
                <h4 className="text-xl font-semibold uppercase  mb-4">
                  {value.title}
                </h4>
                <p className=" text-slate-400  ">
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- WHY VCNR? --- */}
      <section className="py-24 bg-white reveal">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <span className="text-vcnr-brand text-[12px] font-bold uppercase tracking-[0.1em]">
                Why Choose Us
              </span>
              <h2 className="text-4xl md:text-[3rem]  font-semibold text-slate-900 mb-8 leading-tight">
                A Multidisciplinary <br /> <span className="text-vcnr-brand">Approach to Healing.</span>
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                We break down the silos of traditional medicine. Our specialists
                from different institutes collaborate on complex cases to ensure
                a holistic recovery plan.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  "24/7 Trauma & Emergency",
                  "Paperless Digital Records",
                  "Integrated Rehab Units",
                  "Smart ICU Monitoring",
                  "Home Care Nursing",
                  "Post-Operative Concierge"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="text-vcnr-brand" size={18} />
                    <span className="text-[14px] font-semibold uppercase  text-slate-700">
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-12">
                <Link
                  to="/booking"
                  className="inline-flex items-center gap-3 px-10 py-5 bg-slate-900 text-white rounded-xl text-[12px]  uppercase tracking-widest hover:bg-vcnr-brand transition-all shadow-xl hover:scale-105 active:scale-95"
                >
                  Book A Consultation <Activity size={16} />
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src={why1}
                  className="rounded-2xl h-80 w-full object-cover shadow-lg"
                  alt="Hospital Hub"
                />
                <img
                  src={why2}
                  className="rounded-2xl h-48 w-full object-cover shadow-lg"
                  alt="Lab"
                />
              </div>
              <div className="space-y-4 pt-12">
                <img
                  src={why3}
                  className="rounded-2xl h-48 w-full object-cover shadow-lg"
                  alt="Doctors"
                />
                <img
                  src={why4}
                  className="rounded-2xl h-80 w-full object-cover shadow-lg"
                  alt="Care"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- STATISTICS SECTION --- */}
      <section className="pb-24 bg-slate-50 border-y border-slate-100 reveal">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 text-center">
            {[
              {
                label: "Beds Capacity",
                value: "300+",
                icon: <Building2 size={24} />
              },
              {
                label: "Expert Consultants",
                value: "150+",
                icon: <Users size={24} />
              },
              {
                label: "Clinical Success",
                value: "98%",
                icon: <Activity size={24} />
              },
              {
                label: "Happy Recoveries",
                value: "50k+",
                icon: <Heart size={24} />
              }
            ].map((stat, i) => (
              <div key={i} className="group">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center text-vcnr-brand mx-auto mb-6 group-hover:bg-vcnr-brand group-hover:text-white transition-all duration-500 group-hover:rotate-12">
                  {stat.icon}
                </div>
                <div className="text-4xl font-bold text-slate-900 mb-2 ">
                  {stat.value}
                </div>
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- INFRASTRUCTURE HIGHLIGHT --- */}
      <section className="py-24 bg-white reveal">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-slate-900 rounded-[3rem] p-12 md:p-20 text-white relative overflow-hidden group">
            <div className="absolute inset-0 opacity-20 hospital-grid group-hover:scale-110 transition-transform duration-[20s]"></div>
            <div className="relative z-10 grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-vcnr-brand text-[12px] font-bold uppercase tracking-[0.1em]">
                  Infrastructure
                </span>
                <h2 className="text-4xl md:text-[3rem]  font-semibold text-slate-900 mb-8 leading-tight text-white">
                  Smart Clinical Ecosystem
                </h2>
                <p className=" text-slate-200 text-lg mb-8  ">
                  Our facilities are designed to reduce patient stress while
                  maximizing clinical efficiency. From ergonomic ward designs to
                  automated pharmacy systems, every inch of VCNR is optimized
                  for healing.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-center gap-3 text-sm  uppercase  text-white/80">
                    <div className="h-1.5 w-1.5 rounded-full bg-vcnr-brand"></div>
                    Ultra-Modern Laminar Flow OTs
                  </li>
                  <li className="flex items-center gap-3 text-sm  uppercase  text-white/80">
                    <div className="h-1.5 w-1.5 rounded-full bg-vcnr-brand"></div>
                    Advanced GE MRI & CT Suites
                  </li>
                  <li className="flex items-center gap-3 text-sm  uppercase  text-white/80">
                    <div className="h-1.5 w-1.5 rounded-full bg-vcnr-brand"></div>
                    24/7 Automated Blood Bank
                  </li>
                </ul>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 border-2 border-vcnr-brand rounded-2xl group-hover:rotate-3 transition-transform duration-700"></div>
                <img
                  src={infra}
                  className="relative rounded-xl shadow-2xl w-full h-80 object-cover"
                  alt="Medical Tech"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- FINAL CTA --- */}
      <section className="py-24 bg-white reveal">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className="text-4xl md:text-[3rem]  font-semibold text-slate-900 mb-8 leading-tight">
            Experience Global <span className="text-vcnr-brand">Healthcare.</span> 
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Join thousands of families who trust VCNR for their healthcare
            needs. Our experts are ready to provide the specialized care you
            deserve.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <Link
              to="/booking"
              className="bg-vcnr-brand text-white px-12 py-5 rounded-xl font-semibold uppercase text-[14px]  shadow-2xl shadow-vcnr-brand/30 hover:bg-slate-900 transition-all hover:scale-110 active:scale-95"
            >
              Request Appointment
            </Link>
            <Link
              to="/doctors"
              className="bg-white text-slate-900 border-2 border-slate-200 px-12 py-5 rounded-xl font-semibold uppercase text-[14px]  hover:border-vcnr-brand hover:text-vcnr-brand transition-all hover:scale-110 active:scale-95"
            >
              Full Specialist Directory
            </Link>
          </div>
        </div>
      </section>

    <div className="bg-slate-900 py-10 px-6">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-6">
                <div className="w-12 h-12 bg-vcnr-brand/20 rounded-xl flex items-center justify-center text-vcnr-brand">
                  <MapPin size={24} />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-slate-400 uppercase tracking-widest mb-1">
                    Facility Address
                  </div>
                  <div className="  text-white uppercase ">
                    Opp Binnamangala Nursery, B.H. Road, Nelamangala - 562123,
                    Bangalore.
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <a
                  href="/booking"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-3 bg-white text-slate-900 rounded-xl text-[12px] font-bold uppercase  hover:bg-vcnr-brand hover:text-white transition-all"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
    </div>
  )
}

export default About
